import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useI18n from "../../i18n/useI18n";

const THEME_KEY = "hookora_theme";

const HomeHero = () => {
  const { t } = useI18n();

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 900 : false
  );

  const trustPoints = useMemo(
    () => [
      tt("homeHero.trustPoints.tools", "22 Studio tools across one workflow"),
      tt("homeHero.trustPoints.free", "Start free with core tools"),
      tt(
        "homeHero.trustPoints.upgrade",
        "Upgrade for premium and strategic tools"
      ),
    ],
    [t]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      sectionBg: "transparent",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      badgeBg: isDark ? "rgba(37,99,235,0.16)" : "#dbeafe",
      badgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      primaryCardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      secondaryCardBg: isDark ? "rgba(30,41,59,0.72)" : "#f8fafc",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      shadow: isDark
        ? "0 20px 50px rgba(2, 6, 23, 0.42)"
        : "0 20px 50px rgba(15, 23, 42, 0.08)",
      metricBg: isDark ? "rgba(15,23,42,0.9)" : "#ffffff",
      metricText: isDark ? "#f8fafc" : "#111827",
      metricMuted: isDark ? "#94a3b8" : "#6b7280",
      freeBg: isDark ? "rgba(120,53,15,0.14)" : "#fff7ed",
      freeBorder: isDark ? "rgba(251,191,36,0.22)" : "#fdba74",
      freeText: isDark ? "#fde68a" : "#9a3412",
      creatorBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      creatorBorder: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
      creatorText: isDark ? "#bfdbfe" : "#1d4ed8",
      proBg: isDark ? "rgba(109,40,217,0.14)" : "#f5f3ff",
      proBorder: isDark ? "rgba(216,180,254,0.22)" : "#d8b4fe",
      proText: isDark ? "#e9d5ff" : "#7c3aed",
    }),
    [isDark]
  );

  return (
    <section
      style={{
        padding: isMobile ? "56px 20px 36px" : "82px 20px 48px",
        background: colors.sectionBg,
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.08fr 0.92fr",
          gap: "30px",
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 14px",
              borderRadius: "999px",
              background: colors.badgeBg,
              color: colors.badgeText,
              fontWeight: "700",
              fontSize: "12px",
              marginBottom: "18px",
              border: isDark ? `1px solid ${colors.border}` : "none",
            }}
          >
            {tt("homeHero.badge", "AI workflow platform for creators")}
          </div>

          <h1
            style={{
              margin: "0 0 16px",
              fontSize: "clamp(2.4rem, 5.4vw, 4.6rem)",
              lineHeight: "1.02",
              letterSpacing: "-0.04em",
              color: colors.text,
              maxWidth: "820px",
            }}
          >
            {tt(
              "homeHero.title",
              "Create stronger content faster with Hookora’s 22-tool Studio"
            )}
          </h1>

          <p
            style={{
              margin: "0 0 24px",
              color: colors.textMuted,
              fontSize: "17px",
              lineHeight: "1.8",
              maxWidth: "760px",
            }}
          >
            {tt(
              "homeHero.description",
              "Start free with core writing tools, then unlock premium workflow tools and advanced strategic tools as you grow. Hookora helps creators generate hooks, titles, captions, hashtags, descriptions, scripts, rewrites, retention intros, voice systems, and more from one cleaner workflow."
            )}
          </p>

          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "22px",
            }}
          >
            <Link
              to="/register"
              style={{
                textDecoration: "none",
                background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                color: "#ffffff",
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
                boxShadow: "0 12px 28px rgba(79, 70, 229, 0.24)",
              }}
            >
              {tt("about.createFreeAccount", "Create Free Account")}
            </Link>

            <Link
              to="/features"
              style={{
                textDecoration: "none",
                background: isDark ? "rgba(15,23,42,0.82)" : "#ffffff",
                color: colors.text,
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
                border: `1px solid ${colors.border}`,
              }}
            >
              {tt("about.exploreFeatures", "Explore Features")}
            </Link>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {trustPoints.map((item) => (
              <div
                key={item}
                style={{
                  padding: "10px 12px",
                  borderRadius: "999px",
                  background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
                  color: colors.textMuted,
                  border: `1px solid ${colors.border}`,
                  fontSize: "13px",
                  fontWeight: "600",
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div
            style={{
              background: colors.primaryCardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: "28px",
              padding: "22px",
              boxShadow: colors.shadow,
              backdropFilter: isDark ? "blur(14px)" : "none",
            }}
          >
            <div
              style={{
                display: "grid",
                gap: "14px",
              }}
            >
              <div
                style={{
                  background: colors.secondaryCardBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "18px",
                  padding: "16px",
                }}
              >
                <div
                  style={{
                    color: colors.metricMuted,
                    fontSize: "12px",
                    fontWeight: "700",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {tt("homeHero.ideaLabel", "Content idea")}
                </div>
                <div
                  style={{
                    color: colors.metricText,
                    fontSize: "18px",
                    fontWeight: "800",
                    lineHeight: "1.45",
                  }}
                >
                  {tt(
                    "homeHero.ideaText",
                    "“Why most creators lose attention in the first 3 seconds”"
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    background: colors.metricBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "18px",
                    padding: "16px",
                  }}
                >
                  <div
                    style={{
                      color: colors.metricMuted,
                      fontSize: "12px",
                      fontWeight: "700",
                      marginBottom: "8px",
                    }}
                  >
                    {tt("homeHero.metrics.tools", "Tools")}
                  </div>
                  <div
                    style={{
                      color: colors.metricText,
                      fontSize: "28px",
                      fontWeight: "900",
                      lineHeight: 1,
                    }}
                  >
                    22
                  </div>
                </div>

                <div
                  style={{
                    background: colors.metricBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "18px",
                    padding: "16px",
                  }}
                >
                  <div
                    style={{
                      color: colors.metricMuted,
                      fontSize: "12px",
                      fontWeight: "700",
                      marginBottom: "8px",
                    }}
                  >
                    {tt("homeHero.metrics.workflow", "Workflow")}
                  </div>
                  <div
                    style={{
                      color: colors.metricText,
                      fontSize: "28px",
                      fontWeight: "900",
                      lineHeight: 1,
                    }}
                  >
                    1
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    background: colors.freeBg,
                    border: `1px solid ${colors.freeBorder}`,
                    borderRadius: "16px",
                    padding: "14px",
                  }}
                >
                  <div
                    style={{
                      color: colors.freeText,
                      fontSize: "12px",
                      fontWeight: "800",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {tt("plans.free", "Free")}
                  </div>
                  <div
                    style={{
                      color: colors.freeText,
                      fontSize: "14px",
                      lineHeight: "1.6",
                      fontWeight: "700",
                    }}
                  >
                    {tt(
                      "featuresHero.cards.free.text",
                      "Hooks, Titles, Captions, Hashtags, Descriptions, and Scripts."
                    )}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.creatorBg,
                    border: `1px solid ${colors.creatorBorder}`,
                    borderRadius: "16px",
                    padding: "14px",
                  }}
                >
                  <div
                    style={{
                      color: colors.creatorText,
                      fontSize: "12px",
                      fontWeight: "800",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {tt("plans.creator", "Creator")}
                  </div>
                  <div
                    style={{
                      color: colors.creatorText,
                      fontSize: "14px",
                      lineHeight: "1.6",
                      fontWeight: "700",
                    }}
                  >
                    {tt(
                      "featuresHero.cards.creator.text",
                      "Premium workflow tools for packaging, rewrites, retention, CTAs, carousels, repurposing, and more."
                    )}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.proBg,
                    border: `1px solid ${colors.proBorder}`,
                    borderRadius: "16px",
                    padding: "14px",
                  }}
                >
                  <div
                    style={{
                      color: colors.proText,
                      fontSize: "12px",
                      fontWeight: "800",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {tt("plans.pro", "Pro")}
                  </div>
                  <div
                    style={{
                      color: colors.proText,
                      fontSize: "14px",
                      lineHeight: "1.6",
                      fontWeight: "700",
                    }}
                  >
                    {tt(
                      "featuresHero.cards.pro.text",
                      "Advanced strategic tools for trend conversion, brand voice training, and content differentiation."
                    )}
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "2px",
                  paddingTop: "14px",
                  borderTop: `1px solid ${colors.border}`,
                  color: colors.textMuted,
                  fontSize: "13px",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <span>{tt("featuresHero.footerStats.tieredAccess", "Tiered access")}</span>
                <span>{tt("featuresHero.footerStats.cleanerWorkflow", "Cleaner workflow")}</span>
                <span>{tt("homeHero.footerScale", "Built to scale")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;